// Agente IA — qué acciones del staff ofrece el panel del ticket.
// Refleja las reglas del backend (serviceDeskAiController) para que un botón
// visible nunca termine en un 409 evitable:
//  • Aprobar: ticket sin aprobación vigente y no resuelto/cerrado.
//  • Retirar aprobación: cualquier ticket aprobado no resuelto/cerrado, en
//    cualquier estado del agente — con el agente trabajando (claimed/planned)
//    es justo cuando retirarla detiene la corrida en su próximo punto de
//    control (heartbeat/reporte → 409), antes del push o del PR.
//  • Reintentar: failed / skipped / pr-open, o una corrida cuyo lease ya
//    venció; nunca con un lease vivo (el backend responde 409 sin `force`) ni
//    en un ticket resuelto/cerrado (hay que reabrirlo primero).
import { tsToDate, type Ticket } from "./serviceDeskService";

export const CLOSED_TICKET_STATUSES = ["resolved", "closed"];
const RUNNING_STATES = ["claimed", "planned"];
const RETRYABLE_STATES = ["failed", "skipped", "pr-open", ...RUNNING_STATES];

export interface AiAgentPanelActions {
  approved: boolean;
  isClosed: boolean;
  /** Un runner tiene el ticket tomado y su lease sigue vigente. */
  leaseLive: boolean;
  canApprove: boolean;
  canRevoke: boolean;
  canRetry: boolean;
  /** Reintentar existe para este estado pero espera a que venza el lease. */
  retryWaitsForLease: boolean;
}

export function getAiAgentPanelActions(
  ticket: Pick<Ticket, "status" | "aiAgent">,
  now: number = Date.now(),
): AiAgentPanelActions {
  const ai = ticket.aiAgent ?? { state: "idle" as const };
  const approved = ai.approved === true && !!ai.approvedBy;
  const isClosed = CLOSED_TICKET_STATUSES.includes(ticket.status);
  const leaseExpires = tsToDate(ai.leaseExpiresAt);
  const leaseLive = RUNNING_STATES.includes(ai.state) && !!leaseExpires && leaseExpires.getTime() > now;
  const retryableState = RETRYABLE_STATES.includes(ai.state);
  return {
    approved,
    isClosed,
    leaseLive,
    canApprove: !approved && !isClosed,
    canRevoke: approved && !isClosed,
    canRetry: !isClosed && retryableState && !leaseLive,
    retryWaitsForLease: !isClosed && retryableState && leaseLive,
  };
}
